import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { appelApi, ErreurApi, EVENEMENT_SESSION_EXPIREE, urlApi } from './api';
import { parametresVueGlobale } from './useVueGlobale';
import type { Profil } from './types';

/**
 * Suivi des réclamations (TF-482). La personne qui réclame dépose et suit les
 * siennes ; l'équipe tient le registre de ses antennes et traite. Statuts,
 * délais, compteurs et gestes possibles sont des lectures de l'API
 * (`domain/reclamations.ts`) : l'écran affiche.
 */

export type StatutReclamation = 'NOUVELLE' | 'EN_INSTRUCTION' | 'CLOTUREE';
export type QualiteReclamant = 'APPRENANT' | 'ENTREPRISE' | 'FORMATEUR' | 'FINANCEUR' | 'AUTRE';
export type ChoixPeriodeReclamations = 'TOUTES' | 'MOIS' | 'MOIS_PRECEDENT' | 'TRIMESTRE' | 'ANNEE' | 'LIBRE';
export type FiltreStatutReclamation = 'TOUS' | 'OUVERTES' | 'HORS_DELAI' | StatutReclamation;
export type GesteReclamation = 'QUALIFIER' | 'NOTER' | 'CLOTURER' | 'ROUVRIR';

/** Bornes des saisies, celles de l'API. */
export const LIMITES_RECLAMATION = {
  objet: { min: 3, max: 150 },
  description: { min: 10, max: 4000 },
  nom: { min: 2, max: 150 },
  contact: 200,
  reponse: { min: 10, max: 4000 },
  action: 2000,
  amelioration: 2000,
  note: { min: 2, max: 4000 },
  motif: { min: 5, max: 1000 },
} as const;

export interface Option<T extends string = string> {
  valeur: T;
  libelle: string;
}

export interface Delai {
  jours: number;
  horsDelai: boolean;
}

export interface LigneRegistre {
  id: string;
  reference: string;
  siteCode: string;
  statut: StatutReclamation;
  statutLibelle: string;
  recueLe: string;
  canalLibelle: string;
  qualite: QualiteReclamant;
  qualiteLibelle: string;
  nomReclamant: string;
  entreprise: string | null;
  formation: string | null;
  objet: string;
  categorie: string | null;
  gravite: { valeur: string; libelle: string } | null;
  responsable: { id: string; nom: string } | null;
  clotureLe: string | null;
  delai: Delai;
}

export interface CandidatResponsable {
  id: string;
  nom: string;
  email: string;
  fonction: string;
}

export interface CompteursReclamations {
  total: number;
  nouvelles: number;
  enInstruction: number;
  cloturees: number;
  horsDelai: number;
  clotureesHorsDelai: number;
  delaiMoyen: number | null;
  delaiCible: number;
}

export interface OptionsRegistre {
  sites: { code: string; nom: string }[];
  periodes: Option<ChoixPeriodeReclamations>[];
  statuts: Option<FiltreStatutReclamation>[];
  qualites: Option<QualiteReclamant>[];
  canaux: Option[];
  categories: Option[];
  gravites: Option[];
  conclusions: Option[];
  formations: { id: string; code: string; titre: string }[];
  responsables: Record<string, CandidatResponsable[]>;
  mentionEquipe: string;
}

export interface ReponseRegistre {
  calculeLe: string;
  periode: { choix: ChoixPeriodeReclamations; du: string | null; au: string | null; libelle: string };
  compteurs: CompteursReclamations;
  lignes: LigneRegistre[];
  options: OptionsRegistre;
}

export interface ChangementReclamation {
  champ: string;
  avant: string | null;
  apres: string | null;
}

export interface EvenementReclamation {
  type: string;
  libelle: string;
  le: string;
  auteur: string | null;
  texte: string | null;
  changements: ChangementReclamation[];
  visibleReclamant: boolean;
}

export interface VueReclamation extends LigneRegistre {
  description: string;
  canal: string;
  contactReclamant: string | null;
  apprenantId: string | null;
  entrepriseId: string | null;
  qualification: { categorie: string | null; gravite: string | null; responsableId: string | null };
  priseEnChargeLe: string | null;
  information: { le: string; confirmeePar: string | null };
  deposee: { le: string; par: string | null };
  cloture: {
    le: string;
    par: string | null;
    conclusion: string;
    conclusionLibelle: string;
    reponse: string;
    actionCorrective: string | null;
    amelioration: string | null;
  } | null;
  historique: EvenementReclamation[];
  gestes: { geste: GesteReclamation; libelle: string; refus: string | null }[];
  responsables: CandidatResponsable[];
}

export interface MaReclamation {
  id: string;
  reference: string;
  statut: StatutReclamation;
  statutLibelle: string;
  recueLe: string;
  siteCode: string;
  objet: string;
  description: string;
  reponse: string | null;
  clotureLe: string | null;
  evenements: { le: string; libelle: string }[];
}

export interface ReponseMesReclamations {
  mention: string;
  sites: { code: string; nom: string }[];
  refusDepot: string | null;
  reclamations: MaReclamation[];
}

export interface FiltresReclamations {
  site: string;
  periode: ChoixPeriodeReclamations;
  du: string;
  au: string;
  statut: FiltreStatutReclamation;
  categorie: string;
  responsable: string;
  recherche: string;
}

/** Qui dépose et suit ses propres réclamations ; l'API garde la route par profil. */
export function estReclamant(profil: Profil | undefined): boolean {
  return profil === 'APPRENANT' || profil === 'FORMATEUR';
}

/** Une période libre incomplète ou à l'envers n'est pas demandée. */
export function periodeIncomplete(filtres: Pick<FiltresReclamations, 'periode' | 'du' | 'au'>): boolean {
  return filtres.periode === 'LIBRE' && (!filtres.du || !filtres.au || filtres.du > filtres.au);
}

function chaine(filtres: FiltresReclamations): string {
  const libre = filtres.periode === 'LIBRE';
  return parametresVueGlobale({
    ...filtres,
    periode: filtres.periode === 'TOUTES' ? '' : filtres.periode,
    statut: filtres.statut === 'TOUS' ? '' : filtres.statut,
    du: libre ? filtres.du : '',
    au: libre ? filtres.au : '',
  });
}

export function useRegistreReclamations(filtres: FiltresReclamations) {
  const parametres = chaine(filtres);
  return useQuery({
    queryKey: ['reclamations', 'registre', parametres],
    queryFn: () => appelApi<ReponseRegistre>(`/reclamations?${parametres}`),
    placeholderData: keepPreviousData,
    enabled: !periodeIncomplete(filtres),
  });
}

export function useReclamation(id: string | null) {
  return useQuery({
    queryKey: ['reclamations', 'fiche', id],
    queryFn: () => appelApi<VueReclamation>(`/reclamations/${id}`),
    enabled: id !== null,
    retry: false,
  });
}

export function useMesReclamations(actif: boolean) {
  return useQuery({
    queryKey: ['reclamations', 'mes'],
    queryFn: () => appelApi<ReponseMesReclamations>('/reclamations/mes'),
    enabled: actif,
  });
}

export function useReclamants(qualite: 'APPRENANT' | 'FORMATEUR' | 'ENTREPRISE' | null, recherche: string) {
  return useQuery({
    queryKey: ['reclamations', 'reclamants', qualite, recherche],
    queryFn: () =>
      appelApi<{ id: string; libelle: string; detail: string | null }[]>(
        `/reclamations/reclamants?${new URLSearchParams({ qualite: qualite ?? '', recherche }).toString()}`,
      ),
    enabled: qualite !== null,
    placeholderData: keepPreviousData,
  });
}

export interface SaisieDepot {
  objet: string;
  description: string;
  site?: string;
  tracabiliteComprise: boolean;
}

export function useDeposerReclamation() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (saisie: SaisieDepot) =>
      appelApi<ReponseMesReclamations>('/reclamations/mes', { methode: 'POST', corps: saisie }),
    onSuccess: (reponse) => client.setQueryData(['reclamations', 'mes'], reponse),
  });
}

export interface SaisieEquipe {
  qualite: QualiteReclamant;
  apprenantId?: string;
  formateurId?: string;
  entrepriseId?: string;
  site?: string;
  nomReclamant?: string;
  contact?: string;
  formationId?: string;
  canal: string;
  recueLe: string;
  objet: string;
  description: string;
  responsableId?: string;
  reclamantInforme: boolean;
}

/** Après un geste, la fiche renvoyée remplace celle en cache et le registre se relit. */
function useGeste<E>(chemin: (id: string) => string) {
  const client = useQueryClient();
  return useMutation({
    mutationFn: ({ id, corps }: { id: string; corps: E }) =>
      appelApi<VueReclamation>(chemin(id), { methode: 'POST', corps }),
    onSuccess: (fiche) => {
      client.setQueryData(['reclamations', 'fiche', fiche.id], fiche);
      void client.invalidateQueries({ queryKey: ['reclamations', 'registre'] });
    },
  });
}

export function useSaisirReclamation() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (saisie: SaisieEquipe) => appelApi<VueReclamation>('/reclamations', { methode: 'POST', corps: saisie }),
    onSuccess: (fiche) => {
      client.setQueryData(['reclamations', 'fiche', fiche.id], fiche);
      void client.invalidateQueries({ queryKey: ['reclamations', 'registre'] });
    },
  });
}

export function useQualifierReclamation() {
  return useGeste<{ categorie: string; gravite: string; responsableId: string }>((id) => `/reclamations/${id}/qualification`);
}

export function useNoterReclamation() {
  return useGeste<{ texte: string }>((id) => `/reclamations/${id}/notes`);
}

export function useCloturerReclamation() {
  return useGeste<{ reponse: string; conclusion: string; actionCorrective?: string; amelioration?: string }>(
    (id) => `/reclamations/${id}/cloture`,
  );
}

export function useRouvrirReclamation() {
  return useGeste<{ motif: string }>((id) => `/reclamations/${id}/reouverture`);
}

/**
 * Exporte le registre en Excel (registre, historique, paramètres) ou en PDF,
 * aux filtres de l'écran. Passe par `fetch` : un refus de l'API s'affiche à
 * l'écran au lieu de s'enregistrer comme un faux fichier.
 */
export async function exporterReclamations(
  format: 'xlsx' | 'pdf',
  filtres: FiltresReclamations,
): Promise<{ nomFichier: string }> {
  const reponse = await fetch(urlApi(`/reclamations/export?${chaine(filtres)}`), {
    method: 'POST',
    credentials: 'include',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ format }),
  });
  if (!reponse.ok) {
    const erreur = (await reponse.json().catch(() => null)) as
      | { erreur?: { code?: string; message?: string; details?: unknown } }
      | null;
    if (reponse.status === 401) window.dispatchEvent(new Event(EVENEMENT_SESSION_EXPIREE));
    throw new ErreurApi(
      reponse.status,
      erreur?.erreur?.code ?? 'ERREUR',
      erreur?.erreur?.message ?? 'L’export n’a pas pu être produit.',
      erreur?.erreur?.details,
    );
  }
  const nomFichier =
    /filename="([^"]+)"/.exec(reponse.headers.get('content-disposition') ?? '')?.[1] ?? `reclamations.${format}`;
  const url = URL.createObjectURL(await reponse.blob());
  const lien = document.createElement('a');
  lien.href = url;
  lien.download = nomFichier;
  document.body.appendChild(lien);
  lien.click();
  document.body.removeChild(lien);
  URL.revokeObjectURL(url);
  return { nomFichier };
}

// ---------------------------------------------------------------------------
// Paramétrage (direction)
// ---------------------------------------------------------------------------

export interface AntenneReclamations {
  code: string;
  nom: string;
  responsable: CandidatResponsable | null;
  responsableInvalide: string | null;
  candidats: CandidatResponsable[];
  regleLe: string | null;
  reglePar: string | null;
}

export interface VueReglagesReclamations {
  delai: { jours: number; parDefaut: boolean; regleLe: string | null; reglePar: string | null; min: number; max: number };
  antennes: AntenneReclamations[];
}

function useReglage<E>(chemin: (entree: E) => string, corps: (entree: E) => unknown) {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (entree: E) => appelApi<VueReglagesReclamations>(chemin(entree), { methode: 'PUT', corps: corps(entree) }),
    onSuccess: () => {
      void client.invalidateQueries({ queryKey: ['parametrage'] });
      void client.invalidateQueries({ queryKey: ['reclamations'] });
    },
  });
}

export function useReglerDelaiReclamations() {
  return useReglage<number>(
    () => '/parametrage/reclamations/delai',
    (jours) => ({ jours }),
  );
}

export function useReglerResponsableReclamations() {
  return useReglage<{ code: string; responsableId: string | null }>(
    ({ code }) => `/parametrage/reclamations/antennes/${encodeURIComponent(code)}`,
    ({ responsableId }) => ({ responsableId }),
  );
}
